import axios, { AxiosResponse } from "axios";

/*
 * Hardened fetch for the survey-definition JSON that the first application
 * screen loads. A real applicant hit a transient CDN 503 here and was thrown
 * straight into the Sentry error boundary (APPLY-5Z). Two fixes live here:
 *
 *  - Cache-bust on the deploy's git rev rather than wall-clock time, so the
 *    URL is stable within a deploy and CloudFront can serve a cached copy
 *    through an origin blip. The JSON only changes when the frontend is
 *    redeployed (it ships with the build).
 *
 *  - Retry a couple of times with short backoff before giving up, so a single
 *    blip doesn't drop the applicant.
 */

/**
 * Cache-bust value for survey-definition URLs: stable within a deploy, but
 * changes on every deploy.
 *
 * VITE_GIT_REV is only injected by the production build (script/build.js); the
 * plain dev server leaves it undefined. So in dev we fall back to wall-clock
 * time and always-bust, which keeps local edits to data/*.json showing up
 * instantly instead of being cached behind a constant URL.
 */
export function surveyCacheBust(): string {
  return import.meta.env.PROD
    ? import.meta.env.VITE_GIT_REV || "prod"
    : String(Date.now());
}

const delay = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Run an async operation, retrying on failure with exponential backoff.
 *
 * Retries `attempts - 1` times (so the default makes up to 3 calls), waiting
 * `baseDelayMs * 2^n` between tries. The final failure is re-thrown so
 * genuinely-broken loads still surface to the caller.
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  attempts = 3,
  baseDelayMs = 300,
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      return await fn();
    } catch (e) {
      lastError = e;
      if (attempt < attempts - 1) {
        await delay(baseDelayMs * 2 ** attempt);
      }
    }
  }
  throw lastError;
}

/**
 * Fetch a survey-definition JSON file with deploy-stable cache-busting and
 * retry-with-backoff. `url` is the bare path (e.g. "/survey-definitions/
 * protege.json"); the cache-bust query is appended here.
 */
export function fetchSurveyDefinition<T = unknown>(
  url: string,
): Promise<AxiosResponse<T>> {
  const bustedUrl = `${url}?v=${surveyCacheBust()}`;
  return retryWithBackoff(() => axios.get<T>(bustedUrl));
}
